import { useState } from "react";
import { Mail, Phone, MapPin } from "lucide-react";
import { motion } from "framer-motion";
import Sidebar from "./Sidebar";
import emailjs from '@emailjs/browser';
import { ToastContainer, toast } from "react-toastify"; // Import react-toastify
import "react-toastify/dist/ReactToastify.css"; // Import the CSS for styling

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [, setIsSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const result = await emailjs.send(
        "service_zqvy7qe", // Replace with your EmailJS Service ID
        "template_vk6y2hj", // Replace with your EmailJS Template ID
        formData,
        "6mlGuVOcstVe5-Lkt" // Replace with your EmailJS Public Key
      );

      console.log("Email sent:", result.text);
      setIsSubmitted(true);
      setFormData({ name: "", email: "", message: "" });

      // Show toast notification for success
      toast.success("Message sent successfully!", {
        position: "top-right",
        autoClose: 3000, // Closes after 3 seconds
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });

      setTimeout(() => setIsSubmitted(false), 3000);
    } catch (error) {
      console.error("Failed to send email:", error);
      setErrorMessage("Failed to send email. Please try again.");

      // Show toast notification for error (optional)
      toast.error("Failed to send email. Please try again.", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  };

  return (
    <>
      <Sidebar className="md:hidden" />
      <section id="contact" className="py-20 bg-white dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4 font-display">
              Contact Us
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300 font-sans max-w-2xl mx-auto">
              Get in touch with us for any queries or feedback
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Form Section */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <form className="space-y-6" onSubmit={handleSubmit}>
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 font-sans">
                    Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 font-sans">
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 dark:text-gray-300 font-sans">
                    Message
                  </label>
                  <textarea
                    id="message"
                    rows={4}
                    value={formData.message}
                    onChange={handleChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    required
                  ></textarea>
                </div>
                <button
                  type="submit"
                  className="w-full bg-emerald-600 text-white px-6 py-3 rounded-lg hover:bg-emerald-700 transition-colors font-sans"
                >
                  Send Message
                </button>
                {/* Removed inline success message since we're using toast */}
                {errorMessage && (
                  <p className="text-center text-red-600 dark:text-red-500 font-sans">
                    {errorMessage}
                  </p>
                )}
              </form>
            </motion.div>

            {/* Contact Info Section */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-8"
            >
              <div className="flex items-start space-x-4">
                <MapPin className="w-6 h-6 text-emerald-600 flex-shrink-0" />
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white font-display">
                    Address
                  </h3>
                  <a
                    href="https://www.google.com/maps/search/?api=1&query=855+Bristol+Rd+South,+Northfield,+Birmingham+B31+2PA"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-600 dark:text-gray-300 font-sans hover:underline"
                  >
                    855 Bristol Rd South, Northfield, Birmingham B31 2PA
                  </a>

                </div>
              </div>
              <div className="flex items-start space-x-4">
                <Phone className="w-6 h-6 text-emerald-600 flex-shrink-0" />
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white font-display">
                    Phone
                  </h3>
                  <a href="tel:07521262119" className="text-gray-600 dark:text-gray-300 font-sans hover:underline">
                    07521 262119
                  </a>
                  <br />
                  <a href="tel:07424762470" className="text-gray-600 dark:text-gray-300 font-sans hover:underline">
                    07424 762470
                  </a>

                </div>
              </div>
              <div className="flex items-start space-x-4">
                <Mail className="w-6 h-6 text-emerald-600 flex-shrink-0" />
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white font-display">
                    Email
                  </h3>
                  <a href="mailto:vgfoodsuk@gmail.com" className="text-gray-600 dark:text-gray-300 font-sans hover:underline">
                    vgfoodsuk@gmail.com
                  </a>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      {/* Add ToastContainer to render toast notifications */}
      <ToastContainer />
    </>
  );
};

export default Contact;